/* Public branded intake landing page.
 * URL: /intake/:productType/:token
 *
 * Shows the link owner's brand (logo, company name, accent color) and a
 * short intake form. On submit, creates a new application under the owner
 * with status = 'submitted_from_link'. The owner completes the app in the
 * portal from there. */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Loader2, CheckCircle2, Sparkles, ShieldCheck, Building2, Send,
  Mail, Phone, User, DollarSign, ArrowRight,
} from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const IntakePublicForm = () => {
  const { productType, token } = useParams();
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    business_name: '',
    email: '',
    phone: '',
    loan_amount: '',
    use_of_funds: '',
    notes: '',
  });

  useEffect(() => {
    const load = async () => {
      try {
        const resp = await axios.get(`${BACKEND_URL}/api/intake/${productType}/${token}`);
        setInfo(resp.data);
      } catch (err) {
        setError(err?.response?.data?.detail || 'Invalid or expired link');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [productType, token]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.first_name || !form.business_name || !form.email) {
      alert('Name, business name, and email are required');
      return;
    }
    setSubmitting(true);
    try {
      const resp = await axios.post(`${BACKEND_URL}/api/intake/${productType}/${token}`, {
        ...form,
        loan_amount: form.loan_amount ? parseFloat(form.loan_amount) : null,
      });
      // Redirect to the FULL borrower completion form. This is the same UX as
      // any borrower-completion link and lets the applicant finish every
      // product-specific field + upload their required documents.
      if (resp.data?.completion_token) {
        window.location.href = `/borrower/${resp.data.completion_token}`;
        return;
      }
      setSuccess(resp.data);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-rose-200 p-8 text-center" data-testid="intake-error-card">
          <div className="mx-auto h-12 w-12 rounded-full bg-rose-100 flex items-center justify-center mb-3">
            <span className="text-rose-600 text-xl">!</span>
          </div>
          <p className="text-lg font-semibold text-slate-900">Link Not Available</p>
          <p className="text-sm text-slate-500 mt-1">{error}</p>
          <p className="text-xs text-slate-400 mt-6">If you received this link recently, please ask your broker to send a new one.</p>
        </div>
      </div>
    );
  }

  const accent = info?.primary_color || '#0f172a';
  const brand = info?.company_name || info?.broker_name || 'Loan Portal';

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-lg w-full bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden" data-testid="intake-success-card">
          <div style={{ backgroundColor: accent }} className="px-6 py-6 flex items-center gap-3">
            {info?.logo_url && (
              <img
                src={`${BACKEND_URL}${info.logo_url}`}
                alt="logo"
                className="h-11 w-11 rounded-lg bg-white p-1 object-contain"
              />
            )}
            <div className="min-w-0">
              <h2 className="text-white font-bold text-lg truncate">{brand}</h2>
              <p className="text-white/80 text-xs">{info.product_label}</p>
            </div>
          </div>
          <div className="p-8 text-center">
            <div className="mx-auto h-14 w-14 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
              <CheckCircle2 className="h-8 w-8 text-emerald-600" />
            </div>
            <p className="text-xl font-bold text-slate-900">Application Received</p>
            <p className="text-sm text-slate-600 mt-2 max-w-sm mx-auto">
              Thanks for starting a <strong>{info.product_label}</strong> application with <strong>{brand}</strong>.
              A representative will reach out at <strong>{form.email}</strong> shortly to finish the details.
            </p>
            <p className="text-xs text-slate-400 mt-6">Reference: {success.application_id}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 py-10 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Branded header */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden" data-testid="intake-form-card">
          <div style={{ backgroundColor: accent }} className="px-6 py-5 flex items-center gap-4">
            <div className="h-14 w-14 rounded-xl bg-white/95 flex items-center justify-center overflow-hidden shrink-0 shadow-sm">
              {info?.logo_url ? (
                <img src={`${BACKEND_URL}${info.logo_url}`} alt="logo" className="max-h-12 max-w-12 object-contain" />
              ) : (
                <Building2 className="h-6 w-6 text-slate-400" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] uppercase tracking-widest text-white/70 font-semibold">Loan Application</p>
              <h1 className="text-xl font-bold text-white truncate" data-testid="intake-brand-name">{brand}</h1>
            </div>
            <div className="hidden md:flex items-center gap-1.5 text-xs text-white/80 shrink-0" title="Encrypted">
              <ShieldCheck className="h-4 w-4" /> Secure
            </div>
          </div>

          <div className="px-6 py-6" style={{ borderTop: `4px solid ${accent}` }}>
            <div className="mb-6">
              <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">You're applying for</p>
              <p className="text-2xl font-bold text-slate-900 mt-1 flex items-center gap-2">
                <Sparkles className="h-4 w-4" style={{ color: accent }} />
                {info?.product_label}
              </p>
              <div className="mt-3 flex items-start gap-2 rounded-md bg-slate-50 border border-slate-200 px-3 py-2">
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-900 text-white shrink-0 mt-0.5">STEP 1 of 2</span>
                <p className="text-xs text-slate-600 leading-relaxed">
                  Tell us a bit about your business to get started. On the next screen you'll complete the full application and upload your documents.
                </p>
              </div>
            </div>

            <form onSubmit={submit} className="space-y-5">
              {/* Contact */}
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="fn" className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                    <User className="h-3 w-3" /> First Name<span className="text-rose-500">*</span>
                  </Label>
                  <Input id="fn" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="mt-1.5 h-9" required data-testid="intake-first-name" />
                </div>
                <div>
                  <Label htmlFor="ln" className="text-xs font-semibold text-slate-700">Last Name</Label>
                  <Input id="ln" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="mt-1.5 h-9" data-testid="intake-last-name" />
                </div>
              </div>

              <div>
                <Label htmlFor="biz" className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                  <Building2 className="h-3 w-3" /> Business Name<span className="text-rose-500">*</span>
                </Label>
                <Input id="biz" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="mt-1.5 h-9" required data-testid="intake-business-name" />
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="em" className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                    <Mail className="h-3 w-3" /> Email<span className="text-rose-500">*</span>
                  </Label>
                  <Input id="em" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 h-9" required data-testid="intake-email" />
                </div>
                <div>
                  <Label htmlFor="ph" className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                    <Phone className="h-3 w-3" /> Phone
                  </Label>
                  <Input id="ph" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(555) 123-4567" className="mt-1.5 h-9" data-testid="intake-phone" />
                </div>
              </div>

              <div>
                <Label htmlFor="amt" className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                  <DollarSign className="h-3 w-3" /> Amount Requested
                </Label>
                <Input id="amt" type="number" step="0.01" value={form.loan_amount} onChange={(e) => setForm({ ...form, loan_amount: e.target.value })} placeholder="e.g. 250000" className="mt-1.5 h-9" data-testid="intake-amount" />
              </div>

              <div>
                <Label htmlFor="uof" className="text-xs font-semibold text-slate-700">Use of Funds</Label>
                <Textarea id="uof" rows={2} value={form.use_of_funds} onChange={(e) => setForm({ ...form, use_of_funds: e.target.value })} placeholder="How will the funds be used?" className="mt-1.5" data-testid="intake-uof" />
              </div>

              <div>
                <Label htmlFor="nt" className="text-xs font-semibold text-slate-700">Anything else we should know?</Label>
                <Textarea id="nt" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional" className="mt-1.5" data-testid="intake-notes" />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-11 text-sm font-semibold"
                style={{ backgroundColor: accent }}
                data-testid="intake-submit-btn"
              >
                {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Preparing your application…</> : <>Continue to Full Application <ArrowRight className="h-4 w-4 ml-2" /></>}
              </Button>
              <p className="text-[11px] text-slate-500 text-center">
                By continuing, you agree to be contacted by <strong>{brand}</strong> about your loan application.
              </p>
            </form>
          </div>
        </div>
        <p className="text-[11px] text-slate-400 text-center mt-4">Powered by Quidity Direct · Encrypted end-to-end</p>
      </div>
    </div>
  );
};

export default IntakePublicForm;
